import { scaleBand, scaleOrdinal, scaleLinear } from 'd3-scale'
import { axisTop, axisLeft } from 'd3-axis'
import { select } from 'd3-selection'
import { stack } from 'd3-shape'
import 'd3-transition'
import { showSVGTooltip, emptyElement } from '../utils'

export const nTotalStudents = 60
export const TEXT_FONT_SIZE = '0.77rem'

export const barHeight = 20

// Using standard widths for charts, automatically scaled by viewBox attribute of SVG
export const chart1Width = 645
export const chart2Width = 430

export const calculatePanel2Height = (subjects) => {
  return subjects.length * barHeight + 150
}

export function getProcessedData(apData) {
  return apData.flatMap((d) => [
    {
      subject: d.subject,
      x_value: 1,
      percentage: d[1] !== undefined && d[1] !== null ? +d[1] : 0,
      nStudents: +d.nStudents,
    },
    {
      subject: d.subject,
      x_value: 2,
      percentage: d[2] !== undefined && d[2] !== null ? +d[2] : 0,
      nStudents: +d.nStudents,
    },
    {
      subject: d.subject,
      x_value: 3,
      percentage: d[3] !== undefined && d[3] !== null ? +d[3] : 0,
      nStudents: +d.nStudents,
    },
    {
      subject: d.subject,
      x_value: 4,
      percentage: d[4] !== undefined && d[4] !== null ? +d[4] : 0,
      nStudents: +d.nStudents,
    },
    {
      subject: d.subject,
      x_value: 5,
      percentage: d[5] !== undefined && d[5] !== null ? +d[5] : 0,
      nStudents: +d.nStudents,
    },
  ])
}

export function schoolAPContextDist(apData) {
  const processedData = getProcessedData(apData)

  const legendData = [
    { color: '#616c77', label: '1' },
    { color: '#acb0b1', label: '2' },
    { color: '#89b8da', label: '3' },
    { color: '#4878a6', label: '4' },
    { color: '#2a5783', label: '5' },
  ]

  const container = document.getElementById('apChart1')
  emptyElement(container)

  const subjects = Array.from(new Set(processedData.map((d) => d.subject)))

  const panel2Height = calculatePanel2Height(subjects)

  const MARGIN = { top: 60, right: 2, bottom: 40, left: 220 }
  const width = chart1Width - MARGIN.left - MARGIN.right
  const height = panel2Height - MARGIN.top - MARGIN.bottom + 5

  const CIRCLE_RADIUS = 8

  const xValues = Array.from(new Set(processedData.map((d) => d.x_value)))

  const color = scaleOrdinal()
    .domain(xValues.map(String))
    .range(legendData.map((d) => d.color))

  const xScale = scaleLinear().domain([0, 100]).range([0, width])
  const totalHeight = subjects.length * barHeight
  const yScale = scaleBand().domain(subjects).range([0, totalHeight]).padding(0.1)

  const stackData = stack()
    .keys(xValues.map(String))
    .value((d, key) => d.find((v) => v.x_value === +key).percentage)(
    subjects.map((subject) =>
      xValues.map((x) => ({
        x_value: x,
        percentage: processedData.find((d) => d.subject === subject && d.x_value === x).percentage,
        subject: subject,
      }))
    )
  )

  const svg = select(container)
    .append('svg')
    .attr(
      'viewBox',
      `0 0 ${width + MARGIN.left + MARGIN.right} ${height + MARGIN.top + MARGIN.bottom}`
    )
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .classed('svg-content-responsive', true)
    .append('g')
    .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

  const legend = svg.append('g').attr('transform', `translate(30, -45)`)
  const legendItemSpacing = 60

  legend
    .selectAll('g')
    .attr('class', 'legend')
    .data(legendData)
    .enter()
    .append('g')
    .attr('transform', (d, i) => `translate(${i * legendItemSpacing}, 0)`)
    .each(function (d) {
      select(this)
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 10)
        .attr('r', CIRCLE_RADIUS)
        .attr('fill', d.color)

      select(this)
        .append('text')
        .attr('x', 15)
        .attr('y', 15)
        .attr('text-anchor', 'start')
        .style('font-size', TEXT_FONT_SIZE)
        .text(d.label)
    })

  const tooltip = select('body').append('div').attr('class', 'cb-tooltip')

  const groups = svg
    .append('g')
    .selectAll('g')
    .data(stackData)
    .join('g')
    .attr('fill', (d) => color(d.key))

  groups
    .selectAll('rect')
    .data((D) => D.map((d) => ((d.key = D.key), d)))
    .join('rect')
    .attr('x', (d) => xScale(d[0]))
    .attr('y', (d) => yScale(d.data[0].subject))
    .attr('height', barHeight - 1)
    .attr('width', (d) => xScale(d[1]) - xScale(d[0]))
    .on('pointerenter', (event, d) => {
      showSVGTooltip(tooltip, {
        [`Students Scoring ${d.data[d.key - 1].x_value}`]: d.data[d.key - 1].percentage + '%',
        'AP Exam': d.data[d.key - 1].subject,
      })
    })
    .on('pointermove', (event) => {
      tooltip.style('top', event.pageY - 10 + 'px').style('left', event.pageX + 10 + 'px')
    })
    .on('pointerleave', () => {
      tooltip.style('visibility', 'hidden')
    })

  groups
    .selectAll('text')
    .data((D) => D.map((d) => ((d.key = D.key), d)))
    .join('text')
    .attr('x', (d) => (xScale(d[0]) + xScale(d[1])) / 2)
    .attr('y', (d) => yScale(d.data[0].subject) + yScale.bandwidth() / 2)
    .attr('dy', '0.35em')
    .style('font-size', TEXT_FONT_SIZE)
    .attr('text-anchor', 'middle')
    .attr('fill', 'white')
    .text((d) => {
      const percentage = d.data[d.key - 1].percentage
      return percentage > 5 ? `${Math.round(percentage)}%` : ''
    })
    .on('pointerenter', (event, d) => {
      showSVGTooltip(tooltip, {
        [`Students Scoring ${d.data[d.key - 1].x_value}`]: d.data[d.key - 1].percentage + '%',
        'AP Exam': d.data[d.key - 1].subject,
      })
    })
    .on('pointermove', (event) => {
      tooltip.style('top', event.pageY - 10 + 'px').style('left', event.pageX + 10 + 'px')
    })
    .on('pointerleave', () => {
      tooltip.style('visibility', 'hidden')
    })

  svg
    .append('g')
    .attr('class', 'xAxis')
    .call(
      axisTop(xScale)
        .ticks(5)
        .tickFormat((d) => d + '%')
    )
    .call((g) => {
      g.selectAll('text').style('font-size', TEXT_FONT_SIZE)
    })
    .call((g) => {
      g.selectAll('.domain').remove()
      const ticks = g.selectAll('.tick text')
      ticks
        .attr('text-anchor', (d, i, nodes) =>
          i === 0 ? 'end' : i === nodes.length - 1 ? 'start' : 'middle'
        )
        .attr('transform', (d, i, nodes) =>
          i === 0
            ? 'translate(20,0)'
            : i === nodes.length - 1
            ? 'translate(-30,0)'
            : 'translate(0,0)'
        )
      g.append('line')
        .attr('x1', xScale.range()[0] - MARGIN.left)
        .attr('x2', xScale.range()[1] + 10)
        .attr('y1', yScale.range()[0])
        .attr('y2', yScale.range()[0])
        .attr('stroke', '#D3D3D3')
      g.append('line')
        .attr('x1', xScale.range()[0] - MARGIN.left)
        .attr('x2', xScale.range()[1] + 10)
        .attr('y1', yScale.range()[1])
        .attr('y2', yScale.range()[1])
        .attr('stroke', '#D3D3D3')
    })

  svg
    .append('g')
    .attr('class', 'yAxis')
    .call(axisLeft(yScale).tickSize(0))
    .call((g) => {
      g.selectAll('text').style('font-size', TEXT_FONT_SIZE)
    })
    .call((g) => {
      g.selectAll('.domain').remove()
      g.append('line')
        .attr('x1', xScale.range()[0])
        .attr('x2', xScale.range()[0])
        .attr('y1', yScale.range()[0] - 50)
        .attr('y2', yScale.range()[1] + 20)
        .attr('stroke', '#D3D3D3')
    })
}

export function schoolAPContextAvg(apData) {
  const legendData = [
    // { color: '#acb0b1', label: 'Below Average' },
    { color: '#4878a6', label: 'Exam Average' },
  ]

  const container = document.getElementById('apChart1')
  emptyElement(container)

  const subjects = Array.from(new Set(apData.map((d) => d.subject)))
  const panel2Height = calculatePanel2Height(subjects)
  const MARGIN = { top: 60, right: 2, bottom: 40, left: 220 }
  const width = chart1Width - MARGIN.left - MARGIN.right
  const height = panel2Height - MARGIN.top - MARGIN.bottom + 5

  const CIRCLE_RADIUS = 8
  const overallAverageClamped = apData.reduce((sum, d) => sum + d.avgScore, 0) / apData.length

  const xScale = scaleLinear().domain([1, 5]).range([0, width])
  // const yScale = scaleBand().domain(subjects).range([0, height]).padding(0.1)
  const totalHeight = subjects.length * barHeight
  const yScale = scaleBand().domain(subjects).range([0, totalHeight]).padding(0.1)

  const svg = select(container)
    .append('svg')
    .attr(
      'viewBox',
      `0 0 ${width + MARGIN.left + MARGIN.right} ${height + MARGIN.top + MARGIN.bottom}`
    )
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .classed('svg-content-responsive', true)
    .append('g')
    .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

  const legend = svg.append('g').attr('transform', `translate(30, -45)`)
  const legendItemSpacing = 120

  legend
    .selectAll('g')
    .attr('class', 'legend')
    .data(legendData)
    .enter()
    .append('g')
    .attr('transform', (d, i) => `translate(${i * legendItemSpacing}, 0)`)
    .each(function (d) {
      select(this)
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 10)
        .attr('r', CIRCLE_RADIUS)
        .attr('fill', d.color)

      select(this)
        .append('text')
        .attr('x', 15)
        .attr('y', 15)
        .attr('text-anchor', 'start')
        .style('font-size', TEXT_FONT_SIZE)
        .text(d.label)
    })

  const tooltip = select('body').append('div').attr('class', 'cb-tooltip')

  const circleGroup = svg.append('g').attr('class', 'circle-group')

  circleGroup
    .selectAll('circle')
    .data(apData)
    .enter()
    .append('circle')
    .attr('cx', (d) => xScale(d.avgScore))
    .attr('cy', (d) => yScale(d.subject) + yScale.bandwidth() / 2)
    .attr('r', 5)
    .attr('fill', (d) => {
      return legendData[0].color
    })
    .on('pointerenter', (event, d) => {
      showSVGTooltip(tooltip, {
        'AP Exam': d.subject,
        '# of Students': d.nStudents,
        '% of Students': d.pStudents,
        'Avg AP Score': d.avgScore,
      })
    })
    .on('pointermove', (event) => {
      tooltip.style('top', event.pageY - 10 + 'px').style('left', event.pageX + 10 + 'px')
    })
    .on('pointerleave', () => {
      tooltip.style('visibility', 'hidden')
    })

  let xCoord = xScale(overallAverageClamped)
  let yCoord = 0

  // const avgGroup = svg
  //   .append('g')
  //   .attr('class', 'avgGroup')
  //   .on('pointerenter', (event, d) => {
  //     showSVGTooltip(tooltip, {
  //       'Average AP Score': parseFloat(overallAverageClamped.toFixed(2)),
  //     })
  //   })
  //   .on('pointermove', (event) => {
  //     tooltip.style('top', event.pageY - 10 + 'px').style('left', event.pageX + 10 + 'px')
  //   })
  //   .on('pointerleave', () => {
  //     tooltip.style('visibility', 'hidden')
  //   })

  // avgGroup
  //   .append('line')
  //   .attr('x1', xCoord)
  //   .attr('x2', xCoord)
  //   .attr('y1', 0)
  //   .attr('y2', yScale.range()[1])
  //   .attr('class', 'avgLine')
  //   .attr('stroke', '#A9A9A9')
  //   .attr('stroke-width', 2)

  // avgGroup
  //   .append('text')
  //   .attr('x', xCoord - 50)
  //   .attr('y', yScale.range()[0] + 15)
  //   .attr('text-anchor', 'start')
  //   .attr('stroke', '#A9A9A9')
  //   .style('font-size', TEXT_FONT_SIZE)
  //   .text('Average')

  // avgGroup
  //   .append('text')
  //   .attr('x', xCoord - 50)
  //   .attr('y', yScale.range()[1] - 5)
  //   .attr('text-anchor', 'start')
  //   .attr('stroke', '#A9A9A9')
  //   .style('font-size', TEXT_FONT_SIZE)
  //   .text('Average')

  svg
    .append('g')
    .attr('class', 'xAxis')
    .call(axisTop(xScale).ticks(5))
    .call((g) => {
      g.selectAll('text').style('font-size', TEXT_FONT_SIZE)
    })
    .call((g) => {
      g.selectAll('.domain').remove()
      const ticks = g.selectAll('.tick text')
      ticks
        .attr('text-anchor', (d, i, nodes) =>
          i === 0 ? 'end' : i === nodes.length - 1 ? 'start' : 'middle'
        )
        .attr('transform', (d, i, nodes) =>
          i === 0
            ? 'translate(10,0)'
            : i === nodes.length - 1
            ? 'translate(-10,0)'
            : 'translate(0,0)'
        )
      g.append('line')
        .attr('x1', xScale.range()[0] - MARGIN.left)
        .attr('x2', xScale.range()[1] + 10)
        .attr('y1', yScale.range()[0])
        .attr('y2', yScale.range()[0])
        .attr('stroke', '#D3D3D3')
      g.append('line')
        .attr('x1', xScale.range()[0] - MARGIN.left)
        .attr('x2', xScale.range()[1] + 10)
        .attr('y1', yScale.range()[1])
        .attr('y2', yScale.range()[1])
        .attr('stroke', '#D3D3D3')
    })

  svg
    .append('g')
    .attr('class', 'yAxis')
    .call(axisLeft(yScale).tickSize(0))
    .call((g) => {
      g.selectAll('text').style('font-size', TEXT_FONT_SIZE)
    })
    .call((g) => {
      g.selectAll('.domain').remove()
      g.append('line')
        .attr('x1', xScale.range()[0])
        .attr('x2', xScale.range()[0])
        .attr('y1', yScale.range()[0] - 50)
        .attr('y2', yScale.range()[1] + 20)
        .attr('stroke', '#D3D3D3')
    })
}
